import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { useAlert } from '../context/AlertContext';
import { useSite } from '../context/SiteContext';
import TabNavigation from '../components/TabNavigation';
import { invalidateLogoCache } from '../utils/pdfBranding';
import { TENANT_LOGO_UPDATED_EVENT } from '../hooks/useTenantLogo';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Save, Users, Building2, Shield, QrCode, Network, ShieldCheck, FileSpreadsheet, History, Image as ImageIcon, Upload, Trash2, Package } from 'lucide-react';
import ProductMaster from './admin/ProductMaster';
import UserManagementTable from '../components/UserManagementTable';
import RoleManagement from './admin/RoleManagement';
import DepartmentManagement from './admin/DepartmentManagement';
import PublicReporting from './admin/PublicReporting';
import OrgHierarchyPage from './admin/OrgHierarchyPage';
import SitePermissions from './admin/SitePermissions';
import WorkforceAccess from './admin/WorkforceAccess';
import HRMSDataImport from './admin/HRMSDataImport';
import AuditHistoryView from './admin/AuditHistoryView';
import { INDUSTRIES, matchIndustry } from '../utils/industryConstants';
import SearchableSelect from '../components/SearchableSelect';
import { SUPPORTED_COUNTRIES, CURRENCIES, LOCALES, timezoneOptions, prefillFromCountry } from '../utils/regional';

const AdminManagement = ({ user, onLogout }) => {
  const { showSuccess, showError } = useAlert();
  const { activeSiteId } = useSite();
  const [activeTab, setActiveTab] = useState('company');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState(null);

  // Branding (tenant logo shown on PDF reports)
  const [branding, setBranding] = useState({ logo_url: null, logo_download_url: null, primary_color: null });
  const [brandingLoading, setBrandingLoading] = useState(false);
  const logoInputRef = React.useRef(null);
  const MAX_LOGO_BYTES = 500 * 1024;
  
  // Company settings
  const [companySettings, setCompanySettings] = useState({
    company_name: '',
    total_employees: 100,
    industry: '',
    address: '',
    contact_email: '',
    contact_phone: '',
    idle_timeout_minutes: 15,
    // India Form 18 specific fields
    factory_license_no: '',
    manager_name: '',
    // Regional settings
    timezone: 'UTC',
    date_format: 'DD MMM YYYY',
    locale: 'en-US',
    // REG-01: tenant country / currency — '' = not set (legacy behaviour)
    country: '',
    currency: '',
    currency_symbol: '',
    // Incident ID settings
    incident_id_prefix: 'INC',
    incident_id_year_digits: 4,
    // PTW Hot Work post-work observation window (minutes)
    hot_work_observation_minutes: 30,
    // PTW closure reminder lead time before expiry (minutes)
    permit_closure_reminder_minutes: 60,
    // Work shifts (name + from/to time) — shown in the PTW shift dropdown
    shifts: [],
    // PTW report masthead document-control fields
    ptw_format_no: 'EHS/PTW/01',
    ptw_issue_rev_no: '01',
    ptw_rev_date: '02-Jun-2026',
    // HIRA register (EHSMS-F-12) document-control fields
    hira_format_no: 'EHSMS-F-12',
    hira_issue_rev_no: '00',
    hira_rev_date: '',
  });

  // Users
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    role: '',
    department_id: '',
    site_node_id: ''
  });

  // Roles from RBAC
  const [roles, setRoles] = useState([]);
  const [sites, setSites] = useState([]);

  useEffect(() => {
    if (user?.role === 'admin' || user?.role === 'Admin' || user?.role === 'hse_manager' || user?.role === 'HSE Manager') {
      fetchCompanySettings();
      fetchRoles();
      fetchSites();
      fetchDepartments();
      if (activeTab === 'company') {
        fetchBranding();
      }
      if (activeTab === 'users') {
        fetchUsers();
      }
    }
  }, [activeTab, user, activeSiteId]);

  const fetchBranding = async () => {
    try {
      const response = await axios.get(`${API}/tenant-admin/branding`);
      if (response.data) {
        setBranding(response.data);
      }
    } catch (err) {
      // Non-fatal — branding may not be configured yet
      console.log('Branding not available:', err?.response?.status);
    }
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!['image/png', 'image/jpeg'].includes(file.type)) {
      showError('Only PNG or JPG images are allowed.');
      if (logoInputRef.current) logoInputRef.current.value = '';
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      showError(`Logo must be ${MAX_LOGO_BYTES / 1024} KB or smaller.`);
      if (logoInputRef.current) logoInputRef.current.value = '';
      return;
    }

    setBrandingLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API}/tenant-admin/branding/logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setBranding(response.data);
      // Refresh the cached logo so the app header picks up the new one
      // without a reload.
      invalidateLogoCache();
      window.dispatchEvent(new Event(TENANT_LOGO_UPDATED_EVENT));
      showSuccess('Company logo updated.');
    } catch (err) {
      showError(err?.response?.data?.detail || 'Failed to upload logo.');
    } finally {
      setBrandingLoading(false);
      if (logoInputRef.current) logoInputRef.current.value = '';
    }
  };

  const handleLogoDelete = async () => {
    if (!window.confirm('Remove the company logo?')) return;
    setBrandingLoading(true);
    try {
      const response = await axios.delete(`${API}/tenant-admin/branding/logo`);
      setBranding(response.data);
      invalidateLogoCache();
      window.dispatchEvent(new Event(TENANT_LOGO_UPDATED_EVENT));
      showSuccess('Company logo removed.');
    } catch (err) {
      showError(err?.response?.data?.detail || 'Failed to remove logo.');
    } finally {
      setBrandingLoading(false);
    }
  };

  const fetchCompanySettings = async () => {
    try {
      // Use PostgreSQL tenant-admin endpoint
      const response = await axios.get(`${API}/tenant-admin/settings`);
      if (response.data) {
        // Normalize industry value from tenant record to match dropdown values
        const data = { ...response.data };
        if (data.industry) {
          data.industry = matchIndustry(data.industry);
        }
        // REG-01: null from the API → '' so the selects show "Not set"
        data.country = data.country || '';
        data.currency = data.currency || '';
        data.currency_symbol = data.currency_symbol || '';
        setCompanySettings(data);
      }
    } catch (err) {
      console.log('No company settings yet');
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await axios.get(`${API}/tenant-admin/roles`);
      setRoles(response.data || []);
      // Set default role to first role if available
      if (response.data && response.data.length > 0 && !newUser.role) {
        setNewUser(prev => ({ ...prev, role: response.data[0].name }));
      }
    } catch (err) {
      console.log('Failed to fetch roles:', err);
    }
  };

  const fetchSites = async () => {
    try {
      const response = await axios.get(`${API}/org/sites`);
      setSites(response.data || []);
    } catch (err) {
      console.log('Failed to fetch sites:', err);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await axios.get(`${API}/departments`);
      setDepartments(response.data.departments || []);
    } catch (err) {
      console.log('Failed to fetch departments:', err);
    }
  };

  const fetchUsers = async () => {
    try {
      // Use PostgreSQL tenant-admin endpoint; scope to the active site when one
      // is selected ("All Sites" => no filter).
      const siteParam = activeSiteId ? `?site_id=${activeSiteId}` : '';
      const response = await axios.get(`${API}/tenant-admin/users${siteParam}`);
      setUsers(response.data);
    } catch (err) {
      const detail = err.response?.data?.detail;

      if (Array.isArray(detail)) {
        setError(detail);
      } else {
        setError('Failed to fetch users');
      }
    };
  };

  const handleSaveCompanySettings = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Use PostgreSQL tenant-admin endpoint
      await axios.put(`${API}/tenant-admin/settings`, companySettings);
      // BUG-006: Show success toast
      showSuccess('Company settings updated successfully!');
      setSuccess('Company settings saved. Reloading page to apply changes…');
      // Update localStorage tenant data so dateUtils picks up changes on reload
      try {
        const currentTenant = JSON.parse(localStorage.getItem('tenant') || '{}');
        currentTenant.timezone = companySettings.timezone;
        currentTenant.date_format = companySettings.date_format;
        currentTenant.locale = companySettings.locale;
        currentTenant.country = companySettings.country || null;
        currentTenant.currency = companySettings.currency || null;
        currentTenant.currency_symbol = companySettings.currency_symbol || null;
        localStorage.setItem('tenant', JSON.stringify(currentTenant));
      } catch (e) {
        console.log('Could not update tenant localStorage:', e);
      }
      // Reload page after 2 seconds to apply new idle timeout setting
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (err) {
      const detail = err.response?.data?.detail;
      // 422 validation details are objects ({loc, msg}) — show their messages
      const msg = Array.isArray(detail)
        ? detail.map((d) => (typeof d === 'string' ? d : (d?.msg || JSON.stringify(d)))).join('; ')
        : (detail || 'Failed to update settings');
      // BUG-006: Show error toast
      showError(msg);
      if (Array.isArray(detail)) {
        setError(detail);
      } else {
        setError(detail || 'Failed to update settings');
      }
      setLoading(false);
    }
  };

  const handleAddUser = async () => {
    // Department is required: incident assignment routes to the Safety Manager
    // who owns the incident's department, so a department-less user cannot
    // participate in that routing.
    if (!newUser.name || !newUser.email || !newUser.role || !newUser.department_id) {
      setError('Name, email, role, and department are required');
      return;
    }

    setLoading(true);
    setError('');
    try {
      // Use PostgreSQL tenant-admin endpoint - password is auto-generated
      const userPayload = {
        name: newUser.name,
        email: newUser.email,
        role: newUser.role,
        department_id: newUser.department_id,
        site_node_id: newUser.site_node_id || null
      };

      const response = await axios.post(`${API}/tenant-admin/users`, userPayload);
      const tempPassword = response.data.temporary_password;
      const emailSent = response.data.email_sent;
      setSuccess(
        emailSent
          ? `User added successfully! Welcome email sent to ${newUser.email}. Temporary password: ${tempPassword}`
          : `User added successfully! Temporary password: ${tempPassword} (Email could not be sent — please share the password manually)`
      );
      setShowAddUser(false);
      setNewUser({ name: '', email: '', role: '', department_id: '', site_node_id: '' });
      fetchUsers();
      setTimeout(() => setSuccess(''), 10000); // Keep visible longer for password copy
    } catch (err) {
      const detail = err.response?.data?.detail;
  
      if (Array.isArray(detail)) {
        setError(detail);
      } else {
        setError(detail || 'Failed to add user');
      }
    } finally {
      setLoading(false);
    }
  };

  // Check for both legacy (lowercase) and new (capitalized) role names
  const isAdmin = user?.role === 'admin' || user?.role === 'Admin';
  const isHSEManager = user?.role === 'hse_manager' || user?.role === 'HSE Manager';
  
  if (!isAdmin && !isHSEManager) {
    return (
      <div className="min-h-screen bg-gray-50">
        <TabNavigation user={user} onLogout={onLogout} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Alert variant="destructive">
            <AlertDescription>You don't have permission to access this page.</AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <TabNavigation user={user} onLogout={onLogout} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-6">Admin Management</h1>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 flex-wrap">
          <button
            onClick={() => setActiveTab('company')}
            className={`px-6 py-3 rounded-lg font-semibold flex items-center gap-2 ${
              activeTab === 'company'
                ? 'bg-[#1B5C94] text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            <Building2 className="w-5 h-5" />
            Company Settings
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`px-6 py-3 rounded-lg font-semibold flex items-center gap-2 ${
              activeTab === 'users'
                ? 'bg-[#1B5C94] text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            <Users className="w-5 h-5" />
            User Management
          </button>
          <button
            onClick={() => setActiveTab('roles')}
            className={`px-6 py-3 rounded-lg font-semibold flex items-center gap-2 ${
              activeTab === 'roles'
                ? 'bg-[#1B5C94] text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            <Shield className="w-5 h-5" />
            Role Management
          </button>
          <button
            onClick={() => setActiveTab('departments')}
            className={`px-6 py-3 rounded-lg font-semibold flex items-center gap-2 ${
              activeTab === 'departments'
                ? 'bg-[#1B5C94] text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            <Building2 className="w-5 h-5" />
            Departments
          </button>
          <button
            onClick={() => setActiveTab('qr-reporting')}
            className={`px-6 py-3 rounded-lg font-semibold flex items-center gap-2 ${
              activeTab === 'qr-reporting'
                ? 'bg-[#1B5C94] text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            <QrCode className="w-5 h-5" />
            QR Reporting
          </button>
          <button
            onClick={() => setActiveTab('org-hierarchy')}
            className={`px-6 py-3 rounded-lg font-semibold flex items-center gap-2 ${
              activeTab === 'org-hierarchy'
                ? 'bg-[#1B5C94] text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            <Network className="w-5 h-5" />
            Org Hierarchy
          </button>
          <button
            onClick={() => setActiveTab('site-permissions')}
            className={`px-6 py-3 rounded-lg font-semibold flex items-center gap-2 ${
              activeTab === 'site-permissions'
                ? 'bg-[#1B5C94] text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            <ShieldCheck className="w-5 h-5" />
            Site Permissions
          </button>
          <button
            onClick={() => setActiveTab('product-master')}
            className={`px-6 py-3 rounded-lg font-semibold flex items-center gap-2 ${
              activeTab === 'product-master'
                ? 'bg-[#1B5C94] text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            <Package className="w-5 h-5" />
            Product Master
          </button>
          <button
            onClick={() => setActiveTab('hrms-import')}
            className={`px-6 py-3 rounded-lg font-semibold flex items-center gap-2 ${
              activeTab === 'hrms-import'
                ? 'bg-[#1B5C94] text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            <FileSpreadsheet className="w-5 h-5" />
            HRMS Data Import
          </button>
          <button
            onClick={() => setActiveTab('workforce-access')}
            className={`px-6 py-3 rounded-lg font-semibold flex items-center gap-2 ${
              activeTab === 'workforce-access'
                ? 'bg-[#1B5C94] text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            <Users className="w-5 h-5" />
            Workforce Access
          </button>
          {/* BUG-265: Admin-facing audit history for SOC 2 CC7.2 */}
          <button
            onClick={() => setActiveTab('audit-history')}
            className={`px-6 py-3 rounded-lg font-semibold flex items-center gap-2 ${
              activeTab === 'audit-history'
                ? 'bg-[#1B5C94] text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            <History className="w-5 h-5" />
            Audit History
          </button>
        </div>

        {success && (
          <Alert className="mb-6 bg-green-50 border-green-200">
            <AlertDescription className="text-green-800">{success}</AlertDescription>
          </Alert>
        )}

        {/* Render errors properly */}
        {error && (
          <Alert className="mb-6" variant="destructive">
            <AlertDescription>
              {Array.isArray(error) ? (
                <div className="space-y-1">
                  {error.map((err, idx) => (
                    <div key={idx}>
                      {err.loc && <strong>{err.loc[err.loc.length - 1]}: </strong>}
                      {err.msg}
                    </div>
                  ))}
                </div>
              ) : (
                error
              )}
            </AlertDescription>
          </Alert>
        )}

        {/* Company Settings Tab */}
        {activeTab === 'company' && (
          <Card>
            <CardHeader>
              <CardTitle>Company Information & Metrics Configuration</CardTitle>
              <p className="text-sm text-gray-600 mt-1">
                These settings are used to calculate safety metrics like LTIFR, TRIFR, and leading indicators
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Company / Factory Name <span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  value={companySettings.company_name}
                  onChange={(e) => setCompanySettings({...companySettings, company_name: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter company or factory name"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Used in statutory reports like India Form 18
                </p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Total Employees <span className="text-red-600">*</span>
                </label>
                <input
                  type="number"
                  value={companySettings.total_employees}
                  onChange={(e) => setCompanySettings({...companySettings, total_employees: parseInt(e.target.value)})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="100"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Used for LTIFR, TRIFR, and other rate calculations
                </p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Industry
                </label>
                <SearchableSelect
                  value={companySettings.industry}
                  onChange={(val) => setCompanySettings({...companySettings, industry: val})}
                  options={INDUSTRIES}
                  placeholder="Search and select industry..."
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Company Address
                </label>
                <textarea
                  value={companySettings.address}
                  onChange={(e) => setCompanySettings({...companySettings, address: e.target.value})}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter company address"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Contact Email
                  </label>
                  <input
                    type="email"
                    value={companySettings.contact_email}
                    onChange={(e) => setCompanySettings({...companySettings, contact_email: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="contact@company.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Contact Phone
                  </label>
                  <input
                    type="tel"
                    value={companySettings.contact_phone}
                    onChange={(e) => setCompanySettings({...companySettings, contact_phone: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="+1 (555) 123-4567"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Session Timeout (minutes) <span className="text-red-600">*</span>
                </label>
                <input
                  type="number"
                  min="15"
                  max="120"
                  value={companySettings.idle_timeout_minutes}
                  onChange={(e) => {
                    const value = parseInt(e.target.value) || 15;
                    setCompanySettings({...companySettings, idle_timeout_minutes: Math.max(15, value)});
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="15"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Users will be automatically logged out after this period of inactivity (SOC 2 CC6.1 compliance). Minimum: 15 minutes, Recommended: 15-30 minutes.
                </p>
              </div>

              {/* India Form 18 Specific Fields — REG-04: India tenants and
                  legacy tenants with no country set only. */}
              {(!companySettings.country || companySettings.country === 'IN') && (
              <div className="border-t pt-6 mt-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">India Statutory Compliance (Form 18)</h3>
                <p className="text-sm text-gray-600 mb-4">
                  These fields are used to auto-populate India Form 18 statutory reports
                </p>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Factory License Number
                    </label>
                    <input
                      type="text"
                      value={companySettings.factory_license_no}
                      onChange={(e) => setCompanySettings({...companySettings, factory_license_no: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Factory license number"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Factory Manager Name
                    </label>
                    <input
                      type="text"
                      value={companySettings.manager_name}
                      onChange={(e) => setCompanySettings({...companySettings, manager_name: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Name of factory manager"
                    />
                  </div>
                </div>
              </div>
              )}

              {/* Regional Settings */}
              <div className="border-t pt-6 mt-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Regional Settings</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Configure country, currency, timezone, date format, and locale for your organization
                </p>

                {/* REG-01: country / currency. Blank country = legacy behaviour. */}
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Country
                    </label>
                    <select
                      value={companySettings.country || ''}
                      onChange={(e) => setCompanySettings(prefillFromCountry(companySettings, e.target.value))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Not set</option>
                      {companySettings.country && !SUPPORTED_COUNTRIES.some((c) => c.code === companySettings.country) && (
                        <option value={companySettings.country}>{companySettings.country}</option>
                      )}
                      {SUPPORTED_COUNTRIES.map((c) => (
                        <option key={c.code} value={c.code}>{c.name}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Currency
                    </label>
                    <select
                      value={companySettings.currency || ''}
                      onChange={(e) => {
                        const code = e.target.value;
                        const cur = CURRENCIES.find((x) => x.code === code);
                        setCompanySettings({ ...companySettings, currency: code, currency_symbol: cur ? cur.symbol : '' });
                      }}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Not set</option>
                      {companySettings.currency && !CURRENCIES.some((c) => c.code === companySettings.currency) && (
                        <option value={companySettings.currency}>{companySettings.currency}</option>
                      )}
                      {CURRENCIES.map((c) => (
                        <option key={c.code} value={c.code}>{c.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Currency Symbol
                    </label>
                    <input
                      type="text"
                      maxLength={5}
                      value={companySettings.currency_symbol || ''}
                      onChange={(e) => setCompanySettings({ ...companySettings, currency_symbol: e.target.value.slice(0, 5) })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g. RM"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Timezone
                    </label>
                    <SearchableSelect
                      value={companySettings.timezone}
                      onChange={(val) => setCompanySettings({ ...companySettings, timezone: val || 'UTC' })}
                      options={timezoneOptions(companySettings.timezone)}
                      placeholder="Search and select timezone..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Date Format
                    </label>
                    <select
                      value={companySettings.date_format}
                      onChange={(e) => setCompanySettings({...companySettings, date_format: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="DD MMM YYYY">DD MMM YYYY (01 Jan 2026)</option>
                      <option value="YYYY-MM-DD">YYYY-MM-DD (2026-01-01)</option>
                      <option value="MM/DD/YYYY">MM/DD/YYYY (01/01/2026)</option>
                      <option value="DD/MM/YYYY">DD/MM/YYYY (01/01/2026)</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Locale
                    </label>
                    <select
                      value={companySettings.locale}
                      onChange={(e) => setCompanySettings({...companySettings, locale: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {LOCALES.map((l) => (
                        <option key={l.value} value={l.value}>{l.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    Regional settings apply tenant-wide. Changes will affect all users after their next login.
                  </p>
                </div>
              </div>

              {/* Incident ID Configuration */}
              <div className="border-t pt-6 mt-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Incident ID Format</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Configure how incident report IDs are generated (e.g., {companySettings.incident_id_prefix}-{companySettings.incident_id_year_digits === 4 ? '2026' : '26'}-001)
                </p>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      ID Prefix
                    </label>
                    <input
                      type="text"
                      value={companySettings.incident_id_prefix}
                      onChange={(e) => setCompanySettings({...companySettings, incident_id_prefix: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10)})}
                      placeholder="INC"
                      maxLength={10}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">Letters and numbers only, max 10 characters</p>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Year Format
                    </label>
                    <select
                      value={companySettings.incident_id_year_digits}
                      onChange={(e) => setCompanySettings({...companySettings, incident_id_year_digits: parseInt(e.target.value)})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value={4}>4 digits (2026)</option>
                      <option value={2}>2 digits (26)</option>
                    </select>
                  </div>
                </div>

                <div className="mt-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold">Preview:</span>{' '}
                    <span className="font-mono">{companySettings.incident_id_prefix || 'INC'}-{companySettings.incident_id_year_digits === 4 ? '2026' : '26'}-001</span>
                  </p>
                  <p className="text-xs text-gray-500 mt-1">Sequence resets annually. Numbers auto-increment per incident.</p>
                </div>
              </div>

              {/* Permit to Work — Hot Work observation window */}
              <div className="border-t border-gray-200 pt-6 mt-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-1">Permit to Work</h3>
                <p className="text-sm text-gray-600 mb-4">Post-work fire-watch observation for Hot Work permits before they can be closed.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Hot Work observation window (minutes)</label>
                    <input
                      type="number"
                      min={1}
                      max={1440}
                      value={companySettings.hot_work_observation_minutes ?? 30}
                      onChange={(e) => {
                        const v = parseInt(e.target.value, 10);
                        setCompanySettings({ ...companySettings, hot_work_observation_minutes: isNaN(v) ? '' : Math.max(1, Math.min(1440, v)) });
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    />
                    <p className="text-xs text-gray-500 mt-1">Applies to new Hot Work permits. In-progress permits keep the window they started with. Default 30.</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Closure reminder lead time (minutes)</label>
                    <input
                      type="number"
                      min={5}
                      max={1440}
                      value={companySettings.permit_closure_reminder_minutes ?? 60}
                      onChange={(e) => {
                        const v = parseInt(e.target.value, 10);
                        setCompanySettings({ ...companySettings, permit_closure_reminder_minutes: isNaN(v) ? '' : Math.max(5, Math.min(1440, v)) });
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    />
                    <p className="text-xs text-gray-500 mt-1">Reminds the initiator this many minutes before an active permit's validity ends. Default 60.</p>
                  </div>
                </div>

                {/* PTW report masthead — document control (shown on the PDF header) */}
                <p className="text-sm font-medium text-gray-700 mt-5 mb-2">Report header (document control)</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Format No.</label>
                    <input
                      type="text"
                      value={companySettings.ptw_format_no || ''}
                      onChange={(e) => setCompanySettings({ ...companySettings, ptw_format_no: e.target.value })}
                      placeholder="EHS/PTW/01"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Issue / Rev No.</label>
                    <input
                      type="text"
                      value={companySettings.ptw_issue_rev_no || ''}
                      onChange={(e) => setCompanySettings({ ...companySettings, ptw_issue_rev_no: e.target.value })}
                      placeholder="01"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Rev. Date</label>
                    <input
                      type="text"
                      value={companySettings.ptw_rev_date || ''}
                      onChange={(e) => setCompanySettings({ ...companySettings, ptw_rev_date: e.target.value })}
                      placeholder="02-Jun-2026"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-1">Shown in the top-right of the generated Permit to Work PDF.</p>

                {/* HIRA register masthead — document control (shown on the Excel export header) */}
                <p className="text-sm font-medium text-gray-700 mt-5 mb-2">HIRA register header (document control)</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Format No.</label>
                    <input
                      type="text"
                      value={companySettings.hira_format_no || ''}
                      onChange={(e) => setCompanySettings({ ...companySettings, hira_format_no: e.target.value })}
                      placeholder="EHSMS-F-12"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Issue / Rev No.</label>
                    <input
                      type="text"
                      value={companySettings.hira_issue_rev_no || ''}
                      onChange={(e) => setCompanySettings({ ...companySettings, hira_issue_rev_no: e.target.value })}
                      placeholder="00"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Rev. Date</label>
                    <input
                      type="text"
                      value={companySettings.hira_rev_date || ''}
                      onChange={(e) => setCompanySettings({ ...companySettings, hira_rev_date: e.target.value })}
                      placeholder="e.g., 30-Jul-2026"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-1">Shown in the header row of the HIRA EHSMS-F-12 Excel export.</p>
              </div>

              {/* Work shifts — shown in the PTW shift dropdown */}
              <div className="border-t border-gray-200 pt-6 mt-6">
                <div className="flex items-center justify-between mb-1">
                  <h3 className="text-lg font-semibold text-gray-900">Work Shifts</h3>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setCompanySettings({
                      ...companySettings,
                      shifts: [...(companySettings.shifts || []), { name: '', start_time: '', end_time: '' }],
                    })}
                  >
                    + Add Shift
                  </Button>
                </div>
                <p className="text-sm text-gray-600 mb-3">Define work shifts with their from/to times. These appear in the Permit to Work shift dropdown.</p>
                {(companySettings.shifts || []).length === 0 && (
                  <p className="text-sm text-gray-500">No shifts defined yet.</p>
                )}
                <div className="space-y-2">
                  {(companySettings.shifts || []).map((sh, i) => (
                    <div key={i} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center">
                      <input
                        type="text"
                        value={sh.name || ''}
                        onChange={(e) => {
                          const shifts = [...companySettings.shifts];
                          shifts[i] = { ...shifts[i], name: e.target.value };
                          setCompanySettings({ ...companySettings, shifts });
                        }}
                        placeholder="Shift name (e.g. A / Morning)"
                        className="md:col-span-6 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      />
                      <input
                        type="time"
                        value={sh.start_time || ''}
                        onChange={(e) => {
                          const shifts = [...companySettings.shifts];
                          shifts[i] = { ...shifts[i], start_time: e.target.value };
                          setCompanySettings({ ...companySettings, shifts });
                        }}
                        className="md:col-span-2 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        title="From"
                      />
                      <input
                        type="time"
                        value={sh.end_time || ''}
                        onChange={(e) => {
                          const shifts = [...companySettings.shifts];
                          shifts[i] = { ...shifts[i], end_time: e.target.value };
                          setCompanySettings({ ...companySettings, shifts });
                        }}
                        className="md:col-span-2 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        title="To"
                      />
                      <button
                        type="button"
                        onClick={() => setCompanySettings({
                          ...companySettings,
                          shifts: companySettings.shifts.filter((_, idx) => idx !== i),
                        })}
                        className="md:col-span-2 text-red-600 hover:text-red-800 text-sm justify-self-start"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <Button
                onClick={handleSaveCompanySettings}
                disabled={loading}
                className="w-full md:w-auto"
              >
                {loading ? (
                  'Saving...'
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Company Settings
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Branding Card (shown on company tab) */}
        {activeTab === 'company' && (
          <Card className="mt-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ImageIcon className="w-5 h-5" />
                Branding
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600 mb-4">
                Your company logo will appear in the app header and on the cover page of every generated report. PNG or JPG, up to 500 KB.
              </p>

              <div className="flex flex-col md:flex-row gap-6 items-start">
                <div className="w-full md:w-56 h-28 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50 overflow-hidden">
                  {branding.logo_download_url ? (
                    <img
                      src={branding.logo_download_url}
                      alt="Company logo"
                      className="max-w-full max-h-full object-contain"
                    />
                  ) : (
                    <div className="text-center text-gray-400 text-sm px-3">
                      <ImageIcon className="w-6 h-6 mx-auto mb-1 opacity-60" />
                      No logo uploaded
                    </div>
                  )}
                </div>

                <div className="flex-1 space-y-3">
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/png,image/jpeg"
                    onChange={handleLogoUpload}
                    className="hidden"
                  />
                  <div className="flex flex-wrap gap-2">
                    <Button
                      onClick={() => logoInputRef.current?.click()}
                      disabled={brandingLoading}
                      variant="outline"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      {branding.logo_url ? 'Replace Logo' : 'Upload Logo'}
                    </Button>
                    {branding.logo_url && (
                      <Button
                        onClick={handleLogoDelete}
                        disabled={brandingLoading}
                        variant="outline"
                        className="text-red-600 hover:text-red-700 hover:border-red-300"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Remove
                      </Button>
                    )}
                  </div>
                  {brandingLoading && (
                    <p className="text-xs text-gray-500">Working…</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* User Management Tab */}
        {activeTab === 'users' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900">Users ({users.length})</h2>
              <Button onClick={() => setShowAddUser(!showAddUser)}>
                {showAddUser ? 'Cancel' : '+ Add User'}
              </Button>
            </div>

            {showAddUser && (
              <Card>
                <CardHeader>
                  <CardTitle>Add New User</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Name <span className="text-red-600">*</span>
                      </label>
                      <input
                        type="text"
                        value={newUser.name}
                        onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                        placeholder="John Doe"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Email <span className="text-red-600">*</span>
                      </label>
                      <input
                        type="email"
                        value={newUser.email}
                        onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                        placeholder="john@company.com"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Role <span className="text-red-600">*</span>
                      </label>
                      <select
                        value={newUser.role}
                        onChange={(e) => setNewUser({...newUser, role: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      >
                        <option value="">Select a role...</option>
                        {roles.filter(r => r.is_system).length > 0 && (
                          <optgroup label="System Roles">
                            {roles.filter(r => r.is_system).map(role => (
                              <option key={role.id} value={role.name}>
                                {role.display_name || role.name}
                              </option>
                            ))}
                          </optgroup>
                        )}
                        {roles.filter(r => !r.is_system).length > 0 && (
                          <optgroup label="Custom Roles">
                            {roles.filter(r => !r.is_system).map(role => (
                              <option key={role.id} value={role.name}>
                                {role.display_name || role.name}
                              </option>
                            ))}
                          </optgroup>
                        )}
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        Select the appropriate role for this user
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Department <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={newUser.department_id}
                        onChange={(e) => setNewUser({...newUser, department_id: e.target.value})}
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      >
                        <option value="">— Select department —</option>
                        {departments.map((d) => (
                          <option key={d.id} value={d.id}>{d.name}</option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        Incidents in this department are routed to its Safety Manager.
                      </p>
                    </div>

                    {/* Show site selection for site-scoped roles */}
                    {(() => {
                      const selectedRole = roles.find(r => r.name === newUser.role);
                      const isSiteScoped = selectedRole && selectedRole.scope_type !== 'corporate';
                      return isSiteScoped ? (
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Site
                          </label>
                          <select
                            value={newUser.site_node_id}
                            onChange={(e) => setNewUser({...newUser, site_node_id: e.target.value})}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                          >
                            <option value="">All Sites</option>
                            {sites.map(site => (
                              <option key={site.id} value={site.id}>
                                {site.name}
                              </option>
                            ))}
                          </select>
                          <p className="text-xs text-gray-500 mt-1">
                            Assign user to a specific site or leave as "All Sites"
                          </p>
                        </div>
                      ) : null;
                    })()}
                  </div>

                  <Button onClick={handleAddUser} disabled={loading}>
                    {loading ? 'Adding...' : 'Add User'}
                  </Button>
                </CardContent>
              </Card>
            )}

            {users.length > 0 && (
              <UserManagementTable
                users={users}
                departments={departments}
                onUpdate={fetchUsers}
              />
            )}
          </div>
        )}

        {/* Role Management Tab */}
        {activeTab === 'roles' && (
          <RoleManagement user={user} onLogout={onLogout} hideNavigation={true} />
        )}

        {activeTab === 'departments' && (
          <DepartmentManagement />
        )}

        {/* QR Anonymous Reporting Tab */}
        {activeTab === 'qr-reporting' && (
          <PublicReporting user={user} />
        )}

        {/* Org Hierarchy Tab */}
        {activeTab === 'org-hierarchy' && (
          <OrgHierarchyPage user={user} onLogout={onLogout} hideNavigation={true} />
        )}

        {/* Site Permissions Tab */}
        {activeTab === 'site-permissions' && (
          <SitePermissions user={user} onLogout={onLogout} hideNavigation={true} />
        )}

        {/* Product Master Tab */}
        {activeTab === 'product-master' && (
          <ProductMaster />
        )}

        {/* HRMS Data Import Tab */}
        {activeTab === 'hrms-import' && (
          <HRMSDataImport user={user} />
        )}

        {/* Workforce Edit-Access Requests Tab */}
        {activeTab === 'workforce-access' && (
          <WorkforceAccess user={user} onLogout={onLogout} hideNavigation={true} />
        )}

        {/* BUG-265: Audit History Tab */}
        {activeTab === 'audit-history' && (
          <AuditHistoryView />
        )}

      </div>
    </div>
  );
};

export default AdminManagement;
